import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  AlertTriangle,
  BadgePercent,
  BarChart3,
  Bot,
  CheckCircle2,
  Clock3,
  Database,
  DollarSign,
  KeyRound,
  List,
  LockKeyhole,
  LogOut,
  Pause,
  Play,
  Plus,
  RefreshCcw,
  Send,
  Server,
  Settings2,
  Shield,
  Terminal,
  Trash2,
  Wifi,
  XCircle
} from "lucide-react";
import "./styles.css";
import { TelegramPanel } from "./TelegramPanel";

/* ── Types (unchanged) ─────────────────────────────────────── */
type AuthSession = {
  authenticated: boolean;
  username: string | null;
  auth_enabled: boolean;
};

type AppInfo = {
  name: string;
  timezone: string;
  checker_enabled: boolean;
  onebot_http_configured: boolean;
  onebot_ws_configured: boolean;
  onebot_ws_connected: boolean;
  onebot_ws_last_error: string | null;
};

type RobotSummary = {
  status: string;
  [key: string]: unknown;
};

type CommandItem = {
  name: string;
  scope: string;
  description: string;
};

type CommandGroup = {
  robot: string;
  role: string;
  commands: CommandItem[];
};

type Sub2Config = {
  id: number;
  provider: "sub2api" | "newapi";
  recharge_url: string | null;
  name: string;
  target_type: string;
  target_id: string;
  target: string;
  base_url: string;
  email: string;
  enabled: boolean;
  created_by: string | null;
  last_checked_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  dependency_counts: Record<string, number>;
  dependency_summary: string;
};

type PriceBinding = {
  id: number;
  sub2_config_id: number;
  sub2_name: string;
  target: string;
  enabled: boolean;
  last_snapshot_at: string | null;
};

type ApiMonitor = {
  id: number;
  name: string;
  sub2_config_id: number | null;
  sub2_name: string;
  group_key: string | null;
  group_name: string | null;
  remark: string | null;
  target: string;
  base_url: string;
  model_name: string;
  enabled: boolean;
  status: string;
  last_code: string | null;
  last_error: string | null;
  last_checked_at: string | null;
  last_latency_ms: number | null;
  today_availability: number;
};

type CodexConfig = {
  id: number;
  sub2_config_id: number | null;
  sub2_name: string;
  api_key_id: string | null;
  api_key_name: string | null;
  group_key: string | null;
  group_name: string | null;
  base_url: string;
  model_name: string;
  created_by: string | null;
  enabled: boolean;
  last_used_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
};

type MoneyConfig = {
  id: number;
  name: string;
  sub2_config_id: number | null;
  sub2_name: string;
  base_url: string;
  email: string;
  enabled: boolean;
  alert_user_id: string;
  threshold_enabled: boolean;
  threshold_amount: number | null;
  threshold_alerted: boolean;
  last_balance: number | null;
  last_checked_at: string | null;
  last_error: string | null;
};

type MoneyThresholdPayload = {
  threshold_enabled: boolean;
  threshold_amount: number | null;
};

type BroadcastRun = {
  id: number;
  group_id: string;
  sent_at: string;
  message_preview: string;
  ok: boolean;
  error: string | null;
};

type BroadcastJob = {
  id: number;
  name: string;
  enabled: boolean;
  timezone: string;
  start_time: string;
  end_time: string;
  interval_minutes: number;
  selection_mode: "random" | "round_robin";
  group_ids: string[];
  messages: string[];
  next_run_at: string | null;
  last_sent_at: string | null;
  last_error: string | null;
  recent_runs: BroadcastRun[];
};

type Admin = {
  id: number;
  qq: string;
  created_at: string;
};

type StatusBucket = {
  state: "unknown" | "ok" | "partial" | "down";
};

type StatusWindow = {
  key: string;
  label: string;
  buckets: StatusBucket[];
};

type StatusBar = {
  config_id: number;
  config_name: string;
  status: string;
  success_rate: number;
  windows: StatusWindow[];
};

type RecentMessage = {
  id: number;
  received_at: string;
  message_type: string;
  user_id: string;
  group_id: string | null;
  message: string;
  triggered: boolean;
  trigger_type: string | null;
  reply_preview: string | null;
};

type SendFailure = {
  id: number;
  sent_at: string;
  action: string;
  target_type: string;
  target_id: string;
  message_preview: string;
  error: string | null;
  status_code: number | null;
};

type CenterData = {
  generated_at: string;
  app: AppInfo;
  robots: {
    apimonitorbot: RobotSummary;
    napcat: RobotSummary;
  };
  commands: CommandGroup[];
  apimonitor: {
    sub2_configs: Sub2Config[];
    price_bindings: PriceBinding[];
    api_monitors: ApiMonitor[];
    status_bars: StatusBar[];
    codex_config: CodexConfig | null;
    money_configs: MoneyConfig[];
    broadcast_jobs: BroadcastJob[];
    admins: Admin[];
  };
  events: {
    recent_messages: RecentMessage[];
    recent_send_failures: SendFailure[];
  };
};

type KeyOption = {
  key_id: string;
  name: string;
  group_key: string;
  group_name: string;
  base_url: string | null;
  models: string[];
};

type GroupOption = {
  group_key: string;
  group_name: string;
};

type KeyPayload = {
  sub2_config: Sub2Config;
  keys: KeyOption[];
  groups: GroupOption[];
};

type Notice = {
  tone: "ok" | "warn" | "danger";
  text: string;
} | null;

/* ── Tabs ──────────────────────────────────────────────────── */
const tabs = [
  { key: "overview",    label: "概览",          icon: Activity   },
  { key: "apimonitor",  label: "APIMonitorBot",  icon: Settings2  },
  { key: "broadcasts",   label: "自动消息",         icon: Clock3     },
  { key: "telegram",     label: "TG 转发",          icon: Send       },
  { key: "napcat",      label: "NapCat",         icon: Server     },
  { key: "events",      label: "事件",           icon: List       },
] as const;

type TabKey = (typeof tabs)[number]["key"];

/* ── API helpers ───────────────────────────────────────────── */
class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

type ApiRequestInit = Omit<RequestInit, "body"> & { body?: unknown };

async function apiFetch<T>(path: string, options: ApiRequestInit = {}): Promise<T> {
  const { body: rawBody, headers: rawHeaders, ...rest } = options;
  const headers = new Headers(rawHeaders);
  let body: BodyInit | undefined;
  if (rawBody === undefined || rawBody === null) {
    body = undefined;
  } else if (
    typeof rawBody === "string" ||
    rawBody instanceof FormData ||
    rawBody instanceof URLSearchParams
  ) {
    body = rawBody;
  } else {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(rawBody);
  }
  const response = await fetch(path, { ...rest, body, headers, credentials: "include" });
  if (!response.ok) {
    let message = `请求失败：HTTP ${response.status}`;
    try {
      const payload = await response.json();
      if (typeof payload.detail === "string") message = payload.detail;
    } catch { /* keep fallback */ }
    throw new ApiError(response.status, message);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/* ── App root ──────────────────────────────────────────────── */
function App() {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [center, setCenter] = useState<CenterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<Notice>(null);

  const loadCenter = async () => {
    setLoading(true);
    try {
      const data = await apiFetch<CenterData>("/api/config-center");
      setCenter(data);
      setNotice(null);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setSession({ authenticated: false, username: null, auth_enabled: true });
      } else {
        setNotice({ tone: "danger", text: errorMessage(error) });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    apiFetch<AuthSession>("/api/auth/session")
      .then((data) => {
        if (!alive) return;
        setSession(data);
        if (data.authenticated || !data.auth_enabled) {
          void loadCenter();
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        if (!alive) return;
        setNotice({ tone: "danger", text: errorMessage(error) });
        setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  if (loading && session === null) return <BootScreen />;
  if (!session?.authenticated && session?.auth_enabled !== false)
    return <LoginScreen notice={notice} onLogin={(s) => { setSession(s); void loadCenter(); }} />;

  return (
    <Dashboard
      center={center}
      loading={loading}
      notice={notice}
      setNotice={setNotice}
      reload={loadCenter}
      onLogout={async () => {
        await apiFetch<AuthSession>("/api/auth/logout", { method: "POST" });
        setSession({ authenticated: false, username: null, auth_enabled: true });
        setCenter(null);
      }}
    />
  );
}

/* ── BootScreen ────────────────────────────────────────────── */
function BootScreen() {
  return (
    <div className="boot-screen">
      <RefreshCcw className="spin" size={20} />
      <span>加载配置中心…</span>
    </div>
  );
}

/* ── LoginScreen ───────────────────────────────────────────── */
function LoginScreen({ notice, onLogin }: { notice: Notice; onLogin: (s: AuthSession) => void }) {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [localNotice, setLocalNotice] = useState<Notice>(notice);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setLocalNotice(null);
    try {
      const session = await apiFetch<AuthSession>("/api/auth/login", {
        method: "POST",
        body: { username, password },
      });
      onLogin(session);
    } catch (error) {
      setLocalNotice({ tone: "danger", text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon"><Shield size={22} /></div>
          <div className="login-logo-text">
            <small>state.inian.one</small>
            <h1>机器人配置中心</h1>
          </div>
        </div>
        <div className="login-tags">
          <span>APIMonitorBot</span>
          <span>NapCat</span>
          <span>Sub2API</span>
        </div>
        <form className="login-form" onSubmit={submit}>
          <div className="field">
            <span>用户名</span>
            <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
          </div>
          <div className="field">
            <span>密码</span>
            <input value={password} type="password" onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus />
          </div>
          <button className="btn-primary" type="submit" disabled={busy}>
            <LockKeyhole size={16} />
            {busy ? "登录中…" : "登录"}
          </button>
        </form>
        {localNotice && <NoticeBar notice={localNotice} />}
      </div>
    </main>
  );
}

/* ── Dashboard ─────────────────────────────────────────────── */
function Dashboard({
  center, loading, notice, setNotice, reload, onLogout,
}: {
  center: CenterData | null;
  loading: boolean;
  notice: Notice;
  setNotice: (n: Notice) => void;
  reload: () => Promise<void>;
  onLogout: () => Promise<void>;
}) {
  const [activeTab, setActiveTab] = useState<TabKey>(() => tabs.some(t => t.key === location.hash.slice(1)) ? location.hash.slice(1) as TabKey : "overview");
  const [actionBusy, setActionBusy] = useState("");
  const [keysBySub2, setKeysBySub2] = useState<Record<number, KeyPayload>>({});

  const runAction = async (label: string, action: () => Promise<void>) => {
    setActionBusy(label);
    setNotice(null);
    try {
      await action();
      await reload();
      setNotice({ tone: "ok", text: `${label}完成` });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setNotice({ tone: "danger", text: "登录已失效，请重新登录。" });
      } else {
        setNotice({ tone: "danger", text: errorMessage(error) });
      }
    } finally {
      setActionBusy("");
    }
  };

  const loadKeys = async (sub2Id: number) => {
    if (!sub2Id) return null;
    if (keysBySub2[sub2Id]) return keysBySub2[sub2Id];
    const payload = await apiFetch<KeyPayload>(`/api/center/sub2-configs/${sub2Id}/keys`);
    setKeysBySub2((cur) => ({ ...cur, [sub2Id]: payload }));
    return payload;
  };

  const currentTab = tabs.find((t) => t.key === activeTab);

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-icon"><Shield size={16} /></div>
          <div className="sidebar-brand-text">
            <small>state.inian.one</small>
            <strong>配置中心</strong>
          </div>
        </div>
        <nav className="sidebar-nav" aria-label="主导航">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                type="button"
                className={`nav-item${activeTab === tab.key ? " active" : ""}`}
                onClick={() => { setActiveTab(tab.key); location.hash = tab.key; }}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          {center && <p className="timestamp">{formatDate(center.generated_at)}</p>}
          <button type="button" className="btn-ghost" onClick={() => void reload()} disabled={loading}>
            <RefreshCcw size={15} className={loading ? "spin" : ""} />
            刷新数据
          </button>
          <button type="button" className="btn-ghost danger" onClick={() => void onLogout()}>
            <LogOut size={15} />
            退出登录
          </button>
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <div className="topbar-title">
            <small>{currentTab?.label ?? "配置中心"}</small>
            <h1>机器人配置中心</h1>
          </div>
          <div className="topbar-actions">
            <button type="button" className="btn-outline" onClick={() => void reload()} disabled={loading}>
              <RefreshCcw size={15} className={loading ? "spin" : ""} />刷新
            </button>
            <button type="button" className="btn-outline" onClick={() => void onLogout()}>
              <LogOut size={15} />退出
            </button>
          </div>
        </header>

        <div className="content">
          {notice && <NoticeBar notice={notice} />}
          {center ? (
            <>
              {activeTab === "overview"   && <Overview center={center} />}
              {activeTab === "apimonitor" && (
                <APIMonitorPanel center={center} actionBusy={actionBusy} runAction={runAction} loadKeys={loadKeys} keysBySub2={keysBySub2} />
              )}
              {activeTab === "broadcasts" && (
                <BroadcastPanel jobs={center.apimonitor.broadcast_jobs} actionBusy={actionBusy} runAction={runAction} />
              )}
              {activeTab === "napcat"  && <NapCatPanel center={center} />}
              {activeTab === "telegram" && <TelegramPanel />}
              {activeTab === "events"  && <EventsPanel center={center} />}
            </>
          ) : (
            <div className="empty-panel">
              <AlertTriangle size={18} /><span>还没有拿到配置中心数据</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Overview tab ──────────────────────────────────────────── */
function Overview({ center }: { center: CenterData }) {
  const apiRobot = center.robots.apimonitorbot;
  const napcat = center.robots.napcat;
  const apiDownCount = center.apimonitor.api_monitors.filter((m) => m.status === "down").length;

  const metrics = [
    {
      label: "API 分组监控",
      value: String(apiRobot.api_monitor_count ?? center.apimonitor.api_monitors.length),
      icon: BarChart3,
      tone: apiDownCount ? "danger" : "ok",
    },
    {
      label: "Sub2 登录配置",
      value: String(apiRobot.sub2_count ?? center.apimonitor.sub2_configs.length),
      icon: Database,
      tone: "neutral",
    },
    {
      label: "NapCat 配置",
      value: String(napcat.config_file_count ?? 0),
      icon: Server,
      tone: napcat.config_dir_present ? "ok" : "warn",
    },
  ];

  return (
    <div className="overview-grid">
      <div className="metrics-row">
        {metrics.map((m) => <MetricCard key={m.label} {...m} />)}
      </div>

      <div className="panel">
        <div className="panel-head">
          <div className="panel-head-left">
            <span className="panel-icon"><Bot size={16} /></span>
            <h2>链路状态</h2>
          </div>
          <StatusPill
            tone={center.app.onebot_ws_connected ? "ok" : "warn"}
            text={center.app.onebot_ws_connected ? "OneBot WS 已连接" : "OneBot WS 未连接"}
          />
        </div>
        <div className="robot-row">
          <RobotCard
            title="APIMonitorBot"
            status={center.app.checker_enabled ? "监控运行中" : "监控已暂停"}
            detail={`${center.apimonitor.admins.length} 个管理员 · ${center.apimonitor.price_bindings.length} 个倍率绑定`}
            ok={center.app.checker_enabled}
          />
          <RobotCard
            title="NapCat"
            status={napcat.config_dir_present ? "网关配置可读" : "网关配置未挂载"}
            detail={`${String(napcat.onebot_config_count ?? 0)} 个 OneBot 配置`}
            ok={Boolean(napcat.config_dir_present)}
          />
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <div className="panel-head-left">
            <span className="panel-icon"><List size={16} /></span>
            <h2>命令清单</h2>
          </div>
        </div>
        <CommandCatalog groups={center.commands} />
      </div>
    </div>
  );
}

/* ── APIMonitor panel ──────────────────────────────────────── */
function APIMonitorPanel({
  center, actionBusy, runAction, loadKeys, keysBySub2,
}: {
  center: CenterData;
  actionBusy: string;
  runAction: (label: string, action: () => Promise<void>) => Promise<void>;
  loadKeys: (sub2Id: number) => Promise<KeyPayload | null>;
  keysBySub2: Record<number, KeyPayload>;
}) {
  const sub2Configs = center.apimonitor.sub2_configs;
  const enabledSub2 = sub2Configs.filter((c) => c.enabled);

  const deleteItem = (label: string, path: string) => {
    if (!window.confirm(`确认删除：${label}`)) return;
    void runAction("删除配置", async () => {
      await apiFetch<void>(path, { method: "DELETE" });
    });
  };

  return (
    <div className="config-grid">
      <div className="panel">
        <div className="panel-head">
          <div className="panel-head-left"><span className="panel-icon"><Database size={16} /></span><h2>Sub2 登录配置</h2></div>
        </div>
        <Sub2Form busy={actionBusy === "添加中转"} onSubmit={(body) =>
          runAction("添加中转", async () => { await apiFetch<Sub2Config>("/api/center/sub2-configs", { method: "POST", body }); })
        } />
        <Sub2List items={sub2Configs} busy={Boolean(actionBusy)} onSaveAddress={(item, recharge_url) =>
          runAction("保存充值地址", async () => {
            await apiFetch<Sub2Config>(`/api/center/sub2-configs/${item.id}`, { method: "PATCH", body: { recharge_url } });
          })
        } onDelete={(item) => deleteItem(item.name, `/api/center/sub2-configs/${item.id}`)} />
      </div>

      <div className="panel">
        <div className="panel-head">
          <div className="panel-head-left"><span className="panel-icon"><BadgePercent size={16} /></span><h2>倍率监听</h2></div>
        </div>
        <PriceForm sub2Configs={enabledSub2} busy={actionBusy === "添加倍率监听"} onSubmit={(body) =>
          runAction("添加倍率监听", async () => { await apiFetch<PriceBinding>("/api/center/price-bindings", { method: "POST", body }); })
        } />
        <PriceList items={center.apimonitor.price_bindings} onDelete={(item) =>
          deleteItem(`${item.sub2_name} -> ${item.target}`, `/api/center/price-bindings/${item.id}`)
        } />
      </div>

      <div className="panel" style={{ gridColumn: "1/-1" }}>
        <div className="panel-head">
          <div className="panel-head-left"><span className="panel-icon"><BarChart3 size={16} /></span><h2>API 分组监控</h2></div>
        </div>
        <ApiMonitorForm sub2Configs={enabledSub2} keysBySub2={keysBySub2} loadKeys={loadKeys} busy={actionBusy === "添加API监控"} onSubmit={(body) =>
          runAction("添加API监控", async () => { await apiFetch<ApiMonitor[]>("/api/center/api-monitors", { method: "POST", body }); })
        } />
        <ApiMonitorList
          items={center.apimonitor.api_monitors}
          bars={center.apimonitor.status_bars}
          busy={actionBusy === "暂停监听" || actionBusy === "开启监听"}
          onToggle={(item) => void runAction(item.enabled ? "暂停监听" : "开启监听", async () => {
            await apiFetch<ApiMonitor>(`/api/center/api-monitors/${item.id}`, {
              method: "PATCH",
              body: { enabled: !item.enabled },
            });
          })}
          onDelete={(item) => deleteItem(item.name, `/api/center/api-monitors/${item.id}`)}
        />
      </div>

      <div className="panel">
        <div className="panel-head">
          <div className="panel-head-left"><span className="panel-icon"><Terminal size={16} /></span><h2>Codex CLI 默认应用</h2></div>
        </div>
        <CodexDefaultSummary config={center.apimonitor.codex_config} onDelete={() =>
          deleteItem("Codex 默认应用", "/api/center/codex-default")
        } />
        <CodexDefaultForm sub2Configs={enabledSub2} keysBySub2={keysBySub2} loadKeys={loadKeys} busy={actionBusy === "设置Codex默认应用"} onSubmit={(body) =>
          runAction("设置Codex默认应用", async () => { await apiFetch<CodexConfig>("/api/center/codex-default", { method: "POST", body }); })
        } />
      </div>

      <div className="panel">
        <div className="panel-head">
          <div className="panel-head-left"><span className="panel-icon"><DollarSign size={16} /></span><h2>余额监控</h2></div>
        </div>
        <MoneyForm sub2Configs={enabledSub2} admins={center.apimonitor.admins} busy={actionBusy === "添加余额监控"} onSubmit={(body) =>
          runAction("添加余额监控", async () => { await apiFetch<MoneyConfig[]>("/api/center/money-configs/from-sub2", { method: "POST", body }); })
        } />
        <MoneyList items={center.apimonitor.money_configs}
          onUpdate={(item, body) => runAction("修改余额阈值", async () => { await apiFetch<MoneyConfig>(`/api/center/money-configs/${item.id}`, { method: "PATCH", body }); })}
          onDelete={(item) => deleteItem(item.name, `/api/center/money-configs/${item.id}`)}
        />
      </div>
    </div>
  );
}

/* ── Automatic group messages ─────────────────────────────── */
function BroadcastPanel({ jobs, actionBusy, runAction }: {
  jobs: BroadcastJob[];
  actionBusy: string;
  runAction: (label: string, action: () => Promise<void>) => Promise<void>;
}) {
  const emptyForm = {
    name: "", groupIds: "", startTime: "08:00", endTime: "22:00",
    intervalValue: "2", intervalUnit: "hours", selectionMode: "random", messages: "",
  };
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);

  const edit = (job: BroadcastJob) => {
    const useHours = job.interval_minutes % 60 === 0;
    setEditingId(job.id);
    setForm({
      name: job.name,
      groupIds: job.group_ids.join("\n"),
      startTime: job.start_time,
      endTime: job.end_time,
      intervalValue: String(useHours ? job.interval_minutes / 60 : job.interval_minutes),
      intervalUnit: useHours ? "hours" : "minutes",
      selectionMode: job.selection_mode,
      messages: job.messages.join("\n---\n"),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => { setEditingId(null); setForm(emptyForm); };
  const remove = (job: BroadcastJob) => {
    if (!window.confirm(`确认删除自动消息任务：${job.name}`)) return;
    void runAction("删除自动消息", async () => {
      await apiFetch<void>(`/api/broadcast-jobs/${job.id}`, { method: "DELETE" });
      if (editingId === job.id) reset();
    });
  };

  return (
    <div className="overview-grid">
      <div className="panel">
        <div className="panel-head">
          <div className="panel-head-left"><span className="panel-icon"><Clock3 size={16} /></span><h2>{editingId ? "编辑自动消息" : "新增自动消息"}</h2></div>
          {editingId && <button type="button" className="btn-sm" onClick={reset}>取消编辑</button>}
        </div>
        <form className="tool-form" onSubmit={async (e) => {
          e.preventDefault();
          const interval = Math.max(1, Number(form.intervalValue));
          const body = {
            name: form.name.trim(),
            group_ids: form.groupIds.split(/[\s,，]+/).map((v) => v.trim()).filter(Boolean),
            timezone: "Asia/Shanghai",
            start_time: form.startTime,
            end_time: form.endTime,
            interval_minutes: form.intervalUnit === "hours" ? interval * 60 : interval,
            selection_mode: form.selectionMode,
            messages: form.messages.split(/\n---\n/g).map((v) => v.trim()).filter(Boolean),
            enabled: editingId ? jobs.find((job) => job.id === editingId)?.enabled ?? true : true,
          };
          await runAction(editingId ? "保存自动消息" : "添加自动消息", async () => {
            await apiFetch<BroadcastJob>(editingId ? `/api/broadcast-jobs/${editingId}` : "/api/broadcast-jobs", {
              method: editingId ? "PATCH" : "POST", body,
            });
          });
          reset();
        }}>
          <div className="grid-3">
            <div className="form-label"><span>任务名称</span><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="form-label"><span>开始时间</span><input required type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} /></div>
            <div className="form-label"><span>结束时间</span><input required type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} /></div>
          </div>
          <div className="grid-3">
            <div className="form-label"><span>间隔</span><input required min="1" type="number" value={form.intervalValue} onChange={(e) => setForm({ ...form, intervalValue: e.target.value })} /></div>
            <div className="form-label"><span>间隔单位</span><select value={form.intervalUnit} onChange={(e) => setForm({ ...form, intervalUnit: e.target.value })}><option value="hours">小时</option><option value="minutes">分钟</option></select></div>
            <div className="form-label"><span>内容选择</span><select value={form.selectionMode} onChange={(e) => setForm({ ...form, selectionMode: e.target.value })}><option value="random">随机且避免连续重复</option><option value="round_robin">按顺序轮播</option></select></div>
          </div>
          <div className="grid-2">
            <div className="form-label"><span>群号</span><textarea required value={form.groupIds} onChange={(e) => setForm({ ...form, groupIds: e.target.value })} placeholder="每行一个群号，也可用逗号分隔" /></div>
            <div className="form-label"><span>候选消息</span><textarea required className="broadcast-message-editor" value={form.messages} onChange={(e) => setForm({ ...form, messages: e.target.value })} placeholder={"多条消息之间单独一行输入 ---"} /></div>
          </div>
          <button className="btn-add" type="submit" disabled={Boolean(actionBusy)}><Plus size={15} />{editingId ? "保存任务" : "添加任务"}</button>
        </form>
      </div>

      <div className="panel">
        <div className="panel-head"><div className="panel-head-left"><span className="panel-icon"><Send size={16} /></span><h2>并行任务</h2></div><StatusPill tone="neutral" text={`${jobs.length} 个任务`} /></div>
        <div className="list-stack">
          {jobs.length ? jobs.map((job) => (
            <div className="list-row" key={job.id}>
              <div>
                <div className="row-name">{job.name}<StatusPill tone={job.enabled ? "ok" : "neutral"} text={job.enabled ? "运行中" : "已暂停"} /></div>
                <div className="row-detail">群 {job.group_ids.map((id) => `G${id}`).join("、")} · {job.start_time}-{job.end_time} · 每 {formatInterval(job.interval_minutes)} · {job.messages.length} 条内容</div>
                <div className="row-meta">下次：{formatDate(job.next_run_at)} · 最近：{formatDate(job.last_sent_at)}</div>
                {job.last_error && <div className="row-error">{job.last_error}</div>}
              </div>
              <div className="row-actions">
                <button type="button" className="btn-toggle" title="编辑" onClick={() => edit(job)}><Settings2 size={15} /></button>
                <button type="button" className="btn-toggle" title="立即发送一次" onClick={() => void runAction("立即发送", async () => { await apiFetch(`/api/broadcast-jobs/${job.id}/send-now`, { method: "POST" }); })}><Send size={15} /></button>
                <button type="button" className={`btn-toggle ${job.enabled ? "is-running" : "is-paused"}`} title={job.enabled ? "暂停" : "开启"} onClick={() => void runAction(job.enabled ? "暂停自动消息" : "开启自动消息", async () => { await apiFetch(`/api/broadcast-jobs/${job.id}`, { method: "PATCH", body: { enabled: !job.enabled } }); })}>{job.enabled ? <Pause size={15} /> : <Play size={15} />}</button>
                <button type="button" className="btn-delete" title="删除" onClick={() => remove(job)}><Trash2 size={15} /></button>
              </div>
            </div>
          )) : <div className="empty"><Clock3 size={15} /><span>还没有自动消息任务</span></div>}
        </div>
      </div>
    </div>
  );
}

/* ── NapCat panel ──────────────────────────────────────────── */
function NapCatPanel({ center }: { center: CenterData }) {
  const napcat = center.robots.napcat;
  const configs = Array.isArray(napcat.configs) ? (napcat.configs as Record<string, unknown>[]) : [];
  return (
    <div className="two-col">
      <div className="panel">
        <div className="panel-head">
          <div className="panel-head-left"><span className="panel-icon"><Server size={16} /></span><h2>NapCat 摘要</h2></div>
          <StatusPill tone={napcat.config_dir_present ? "ok" : "warn"} text={napcat.config_dir_present ? "配置可读" : "配置未挂载"} />
        </div>
        <dl className="kv-list">
          <dt>角色</dt><dd>{textValue(napcat.role)}</dd>
          <dt>配置文件</dt><dd>{String(napcat.config_file_count ?? 0)}</dd>
          <dt>OneBot 配置</dt><dd>{String(napcat.onebot_config_count ?? 0)}</dd>
          <dt>NapCat 配置</dt><dd>{String(napcat.napcat_config_count ?? 0)}</dd>
        </dl>
      </div>
      <div className="panel">
        <div className="panel-head">
          <div className="panel-head-left"><span className="panel-icon"><Wifi size={16} /></span><h2>网关配置文件</h2></div>
        </div>
        <div className="list-stack">
          {configs.length ? configs.map((cfg) => (
            <div className="list-row" key={String(cfg.file)}>
              <div>
                <div className="row-name">{String(cfg.file)}</div>
                <div className="row-detail">账号：{textValue(cfg.account_hint)} · 端口：{arrayText(cfg.ports)}</div>
                <div className="row-meta">启用项：{arrayText(cfg.enabled_items)}</div>
              </div>
            </div>
          )) : <div className="empty"><XCircle size={15} /><span>没有读取到 NapCat 配置文件</span></div>}
        </div>
      </div>
    </div>
  );
}

/* ── Events panel ──────────────────────────────────────────── */
function EventsPanel({ center }: { center: CenterData }) {
  return (
    <div className="two-col">
      <div className="panel">
        <div className="panel-head">
          <div className="panel-head-left"><span className="panel-icon"><Activity size={16} /></span><h2>最近消息</h2></div>
        </div>
        <div className="list-stack">
          {center.events.recent_messages.length ? center.events.recent_messages.map((msg) => (
            <div className="list-row" key={msg.id}>
              <div>
                <div className="row-name">
                  {msg.message_type} · {msg.group_id ? `G${msg.group_id}` : `P${msg.user_id}`}
                  <span className={`pill ${msg.triggered ? "ok" : "neutral"}`} style={{ marginLeft: "4px" }}>
                    {msg.triggered ? msg.trigger_type ?? "已触发" : "未触发"}
                  </span>
                </div>
                <div className="row-detail">{shortText(msg.message, 120)}</div>
                <div className="row-meta">{formatDate(msg.received_at)}</div>
              </div>
            </div>
          )) : <div className="empty"><XCircle size={15} /><span>没有最近消息</span></div>}
        </div>
      </div>
      <div className="panel">
        <div className="panel-head">
          <div className="panel-head-left"><span className="panel-icon"><AlertTriangle size={16} /></span><h2>发送失败</h2></div>
        </div>
        <div className="list-stack">
          {center.events.recent_send_failures.length ? center.events.recent_send_failures.map((item) => (
            <div className="list-row danger-row" key={item.id}>
              <div>
                <div className="row-name">{item.action} · {item.target_type}:{item.target_id}</div>
                <div className="row-detail">{shortText(item.error || item.message_preview, 140)}</div>
                <div className="row-meta">{formatDate(item.sent_at)} · HTTP {item.status_code ?? "-"}</div>
              </div>
            </div>
          )) : <div className="empty"><XCircle size={15} /><span>没有发送失败记录</span></div>}
        </div>
      </div>
    </div>
  );
}

/* ── Sub2Form ──────────────────────────────────────────────── */
function Sub2Form({ busy, onSubmit }: {
  busy: boolean;
  onSubmit: (body: { provider: "sub2api" | "newapi"; name: string; base_url: string; email: string; password: string; recharge_url: string; enabled: boolean }) => Promise<void>;
}) {
  const [form, setForm] = useState({ provider: "sub2api" as "sub2api" | "newapi", name: "", base_url: "", email: "", password: "", recharge_url: "" });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await onSubmit({ ...form, enabled: true });
    setForm({ provider: form.provider, name: "", base_url: "", email: "", password: "", recharge_url: "" });
  };
  return (
    <form className="tool-form" onSubmit={submit}>
      <div className="grid-2">
        <SelectField label="平台类型" value={form.provider} onChange={(provider) => setForm({ ...form, provider: provider as "sub2api" | "newapi" })} options={[{ value: "sub2api", label: "Sub2API" }, { value: "newapi", label: "New API" }]} required />
        <div className="form-label"><span>中转名称</span>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="form-label"><span>登录地址</span>
          <input required value={form.base_url} placeholder="https://..." onChange={(e) => setForm({ ...form, base_url: e.target.value })} />
        </div>
        <div className="form-label"><span>{form.provider === "newapi" ? "登录账号" : "登录邮箱"}</span>
          <input required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="form-label"><span>密码</span>
          <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <label className="form-label"><span>充值地址（可选）</span>
          <input type="url" maxLength={2048} value={form.recharge_url} placeholder="https://..." onChange={(e) => setForm({ ...form, recharge_url: e.target.value })} />
        </label>
      </div>
      <button className="btn-add" type="submit" disabled={busy}>
        <Plus size={15} />添加并测试登录
      </button>
    </form>
  );
}

/* ── PriceForm ─────────────────────────────────────────────── */
function PriceForm({ sub2Configs, busy, onSubmit }: {
  sub2Configs: Sub2Config[];
  busy: boolean;
  onSubmit: (body: { sub2_config_id: number; target: string }) => Promise<void>;
}) {
  const [sub2Id, setSub2Id] = useState("");
  const [target, setTarget] = useState("");
  return (
    <form className="tool-form" onSubmit={async (e) => {
      e.preventDefault();
      await onSubmit({ sub2_config_id: Number(sub2Id), target: target.trim().toUpperCase() });
      setTarget("");
    }}>
      <div className="grid-2">
        <SelectField label="中转配置" value={sub2Id} onChange={setSub2Id} options={sub2Configs.map(optionFromSub2)} required />
        <div className="form-label"><span>通知对象</span>
          <input required value={target} placeholder="G群号 或 PQQ号" onChange={(e) => setTarget(e.target.value)} />
        </div>
      </div>
      <button className="btn-add" type="submit" disabled={busy || !sub2Configs.length}>
        <Plus size={15} />添加倍率监听
      </button>
    </form>
  );
}

/* ── ApiMonitorForm ────────────────────────────────────────── */
function ApiMonitorForm({ sub2Configs, keysBySub2, loadKeys, busy, onSubmit }: {
  sub2Configs: Sub2Config[];
  keysBySub2: Record<number, KeyPayload>;
  loadKeys: (sub2Id: number) => Promise<KeyPayload | null>;
  busy: boolean;
  onSubmit: (body: { sub2_config_id: number; group_keys: string[]; model_name: string; target: string; remarks: string[]; }) => Promise<void>;
}) {
  const [sub2Id, setSub2Id] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [modelName, setModelName] = useState("");
  const [target, setTarget] = useState("");
  const [remarks, setRemarks] = useState("");
  const numericSub2Id = Number(sub2Id);
  const payload = keysBySub2[numericSub2Id];
  const groups = payload?.groups ?? [];
  const modelSuggestions = useMemo(() => {
    const names = new Set<string>();
    payload?.keys.forEach((k) => k.models.slice(0, 8).forEach((m) => names.add(m)));
    return Array.from(names).slice(0, 10);
  }, [payload]);

  useEffect(() => {
    if (numericSub2Id) void loadKeys(numericSub2Id);
    setSelectedGroups([]);
  }, [numericSub2Id]);

  return (
    <form className="tool-form" onSubmit={async (e) => {
      e.preventDefault();
      await onSubmit({
        sub2_config_id: numericSub2Id,
        group_keys: selectedGroups,
        model_name: modelName.trim(),
        target: target.trim().toUpperCase(),
        remarks: remarks.split("\n").map((l) => l.trim()),
      });
      setSelectedGroups([]); setTarget(""); setRemarks("");
    }}>
      <div className="grid-3">
        <SelectField label="中转配置" value={sub2Id} onChange={setSub2Id} options={sub2Configs.map(optionFromSub2)} required />
        <div className="form-label"><span>监控模型</span>
          <input required value={modelName} onChange={(e) => setModelName(e.target.value)} />
        </div>
        <div className="form-label"><span>通知对象</span>
          <input required value={target} placeholder="G群号 或 PQQ号" onChange={(e) => setTarget(e.target.value)} />
        </div>
      </div>
      {modelSuggestions.length > 0 && (
        <div className="chip-row">
          {modelSuggestions.map((m) => (
            <button key={m} type="button" className="chip" onClick={() => setModelName(m)}>{m}</button>
          ))}
        </div>
      )}
      <CheckboxGrid
        title="可监控分组"
        emptyText={sub2Id ? "正在读取或没有可用分组" : "先选择中转配置"}
        options={groups.map((g) => ({ value: g.group_key, label: g.group_name }))}
        values={selectedGroups}
        onChange={setSelectedGroups}
      />
      <div className="form-label"><span>备注</span>
        <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="按分组顺序一行一个，可留空" />
      </div>
      <button className="btn-add" type="submit" disabled={busy || !selectedGroups.length}>
        <Plus size={15} />添加 API 监控
      </button>
    </form>
  );
}

/* ── CodexDefaultForm ──────────────────────────────────────── */
function CodexDefaultForm({ sub2Configs, keysBySub2, loadKeys, busy, onSubmit }: {
  sub2Configs: Sub2Config[];
  keysBySub2: Record<number, KeyPayload>;
  loadKeys: (sub2Id: number) => Promise<KeyPayload | null>;
  busy: boolean;
  onSubmit: (body: { sub2_config_id: number; group_key: string; model_name?: string }) => Promise<void>;
}) {
  const [sub2Id, setSub2Id] = useState("");
  const [groupKey, setGroupKey] = useState("");
  const [modelName, setModelName] = useState("");
  const numericSub2Id = Number(sub2Id);
  const payload = keysBySub2[numericSub2Id];
  const groups = payload?.groups ?? [];
  const modelSuggestions = useMemo(() => {
    const names = new Set<string>();
    payload?.keys.forEach((k) => k.models.slice(0, 8).forEach((m) => names.add(m)));
    return Array.from(names).slice(0, 10);
  }, [payload]);

  useEffect(() => {
    if (numericSub2Id) void loadKeys(numericSub2Id);
    setGroupKey("");
  }, [numericSub2Id]);

  return (
    <form className="tool-form" onSubmit={async (e) => {
      e.preventDefault();
      if (!groupKey) return;
      await onSubmit({
        sub2_config_id: numericSub2Id,
        group_key: groupKey,
        model_name: modelName.trim() || undefined,
      });
    }}>
      <div className="grid-3">
        <SelectField label="中转配置" value={sub2Id} onChange={setSub2Id} options={sub2Configs.map(optionFromSub2)} required />
        <SelectField label="Codex 分组" value={groupKey} onChange={setGroupKey}
          options={groups.map((g) => ({ value: g.group_key, label: g.group_name }))} required />
        <div className="form-label"><span>模型（可选）</span>
          <input value={modelName} placeholder="留空使用后端默认模型" onChange={(e) => setModelName(e.target.value)} />
        </div>
      </div>
      {modelSuggestions.length ? (
        <div className="chip-row">
          {modelSuggestions.map((m) => (
            <button key={m} type="button" className="chip" onClick={() => setModelName(m)}>{m}</button>
          ))}
        </div>
      ) : null}
      <button className="btn-add" type="submit" disabled={busy || !groupKey}>
        <Terminal size={15} />保存 Codex 默认应用
      </button>
    </form>
  );
}

/* ── MoneyForm ─────────────────────────────────────────────── */
function MoneyForm({ sub2Configs, admins, busy, onSubmit }: {
  sub2Configs: Sub2Config[];
  admins: Admin[];
  busy: boolean;
  onSubmit: (body: { sub2_config_ids: number[]; alert_user_id?: string } & MoneyThresholdPayload) => Promise<void>;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [alertUserId, setAlertUserId] = useState(admins[0]?.qq ?? "");
  const [thresholdEnabled, setThresholdEnabled] = useState(true);
  const [thresholdAmount, setThresholdAmount] = useState("10");
  return (
    <form className="tool-form" onSubmit={async (e) => {
      e.preventDefault();
      await onSubmit({
        sub2_config_ids: selected.map(Number),
        alert_user_id: alertUserId.trim() || undefined,
        threshold_enabled: thresholdEnabled,
        threshold_amount: thresholdEnabled ? Number(thresholdAmount) : null,
      });
      setSelected([]);
    }}>
      <CheckboxGrid title="中转配置" emptyText="还没有可用中转配置"
        options={sub2Configs.map((c) => ({ value: String(c.id), label: c.name }))}
        values={selected} onChange={setSelected} />
      <div className="form-label"><span>余额提醒 QQ</span>
        <input value={alertUserId} onChange={(e) => setAlertUserId(e.target.value)} />
      </div>
      <label className="inline-check">
        <input type="checkbox" checked={thresholdEnabled} onChange={(e) => setThresholdEnabled(e.target.checked)} />
        启用阈值提醒
      </label>
      <div className="form-label"><span>余额阈值</span>
        <input type="number" min="0" step="0.01" disabled={!thresholdEnabled}
          required={thresholdEnabled} value={thresholdAmount}
          onChange={(e) => setThresholdAmount(e.target.value)} />
      </div>
      <button className="btn-add" type="submit" disabled={busy || !selected.length}>
        <Plus size={15} />添加余额监控
      </button>
    </form>
  );
}

/* ── List components ───────────────────────────────────────── */
function Sub2List({ items, busy, onSaveAddress, onDelete }: {
  items: Sub2Config[];
  busy: boolean;
  onSaveAddress: (item: Sub2Config, address: string | null) => Promise<void>;
  onDelete: (item: Sub2Config) => void;
}) {
  return (
    <div className="list-stack">
      {items.length ? items.map((item) => (
        <div className="list-row" key={item.id}>
          <div>
            <div className="row-name">
              {item.name}
              <span className="pill neutral">{item.provider === "newapi" ? "New API" : "Sub2API"}</span>
              <span className={`pill ${item.enabled ? "ok" : "neutral"}`}>{item.enabled ? "启用" : "停用"}</span>
            </div>
            <div className="row-detail">{item.base_url} · {item.email}</div>
            <div className="row-meta">
              {item.last_checked_at ? formatDate(item.last_checked_at) : "未检查"}
              {item.dependency_summary ? ` · 依赖：${item.dependency_summary}` : ""}
            </div>
            {item.last_error && <div className="row-error">{item.last_error}</div>}
            <RechargeAddressForm item={item} busy={busy} onSave={onSaveAddress} />
          </div>
          <button type="button" className="btn-delete" title="删除" onClick={() => onDelete(item)}><Trash2 size={15} /></button>
        </div>
      )) : <div className="empty"><XCircle size={15} /><span>还没有中转登录配置</span></div>}
    </div>
  );
}

function RechargeAddressForm({ item, busy, onSave }: {
  item: Sub2Config;
  busy: boolean;
  onSave: (item: Sub2Config, address: string | null) => Promise<void>;
}) {
  const [address, setAddress] = useState(item.recharge_url ?? "");
  useEffect(() => { setAddress(item.recharge_url ?? ""); }, [item.id, item.recharge_url]);
  return (
    <form className="recharge-address-form" onSubmit={async (event) => {
      event.preventDefault();
      await onSave(item, address.trim() || null);
    }}>
      <label className="form-label"><span>充值地址</span>
        <input type="url" maxLength={2048} aria-label={`${item.name} 充值地址`} value={address}
          placeholder="填写后可通过 /充值 获取；留空可清除"
          onChange={(event) => setAddress(event.target.value)} />
      </label>
      <button className="btn-sm" type="submit" disabled={busy || address.trim() === (item.recharge_url ?? "")}>保存</button>
    </form>
  );
}

function PriceList({ items, onDelete }: { items: PriceBinding[]; onDelete: (item: PriceBinding) => void }) {
  return (
    <div className="list-stack">
      {items.length ? items.map((item) => (
        <div className="list-row" key={item.id}>
          <div>
            <div className="row-name">{item.sub2_name}</div>
            <div className="row-detail">{item.target}</div>
            <div className="row-meta">{item.last_snapshot_at ? formatDate(item.last_snapshot_at) : "未保存快照"}</div>
          </div>
          <button type="button" className="btn-delete" title="删除" onClick={() => onDelete(item)}><Trash2 size={15} /></button>
        </div>
      )) : <div className="empty"><XCircle size={15} /><span>还没有倍率监听配置</span></div>}
    </div>
  );
}

function ApiMonitorList({ items, bars, busy, onToggle, onDelete }: {
  items: ApiMonitor[];
  bars: StatusBar[];
  busy: boolean;
  onToggle: (item: ApiMonitor) => void;
  onDelete: (item: ApiMonitor) => void;
}) {
  const barById = new Map(bars.map((b) => [b.config_id, b]));
  return (
    <div className="list-stack">
      {items.length ? items.map((item) => {
        const bar = barById.get(item.id);
        const displayStatus = bar?.status ?? item.status;
        return (
          <div className="list-row" key={item.id}>
            <div>
              <div className="row-name">
                {item.sub2_name || item.name}
                <StatusPill
                  tone={item.enabled ? statusTone(displayStatus) : "neutral"}
                  text={item.enabled ? statusLabel(displayStatus) : "已暂停"}
                />
              </div>
              <div className="row-detail">
                {item.group_name || item.group_key || "默认分组"} · {item.model_name} · {item.target}
                {item.remark ? ` · ${item.remark}` : ""}
              </div>
              <div className="row-meta">
                可用率 {item.today_availability.toFixed(1)}% · {item.last_latency_ms ?? "-"}ms ·{" "}
                {item.last_checked_at ? formatDate(item.last_checked_at) : "未检查"}
              </div>
              {bar && <StatusRail bar={bar} />}
              {item.last_error && <div className="row-error">{shortText(item.last_error, 160)}</div>}
            </div>
            <div className="row-actions">
              <button
                type="button"
                className={`btn-toggle ${item.enabled ? "is-running" : "is-paused"}`}
                title={item.enabled ? "暂停监听" : "开启监听"}
                aria-label={item.enabled ? "暂停监听" : "开启监听"}
                disabled={busy}
                onClick={() => onToggle(item)}
              >
                {item.enabled ? <Pause size={15} /> : <Play size={15} />}
              </button>
              <button type="button" className="btn-delete" title="删除" onClick={() => onDelete(item)}><Trash2 size={15} /></button>
            </div>
          </div>
        );
      }) : <div className="empty"><XCircle size={15} /><span>还没有 API 分组监控</span></div>}
    </div>
  );
}

function CodexDefaultSummary({ config, onDelete }: { config: CodexConfig | null; onDelete: () => void }) {
  if (!config) {
    return (
      <div className="empty" style={{ padding: "12px 16px" }}>
        <XCircle size={15} /><span>还没有 Codex 默认应用。可通过 /codex default 或下方表单设置。</span>
      </div>
    );
  }
  return (
    <div className="summary-strip" style={{ alignItems: "center" }}>
      <Terminal size={16} />
      <div>
        <strong>{config.sub2_name || "中转未关联"} / {config.group_name || config.group_key}</strong>
        <p>{config.model_name} · {config.last_used_at ? `最后调用 ${formatDate(config.last_used_at)}` : "还未调用"}</p>
        <p>/codex 开启会话；/codex end 关闭。文件目录：/opt/codex-qq</p>
        {config.last_error && <div className="row-error">{shortText(config.last_error, 160)}</div>}
      </div>
      <button type="button" className="btn-delete" title="删除 Codex 默认应用" onClick={onDelete}><Trash2 size={15} /></button>
    </div>
  );
}

function MoneyList({ items, onUpdate, onDelete }: {
  items: MoneyConfig[];
  onUpdate: (item: MoneyConfig, body: MoneyThresholdPayload) => Promise<void>;
  onDelete: (item: MoneyConfig) => void;
}) {
  return (
    <div className="list-stack">
      {items.length ? items.map((item) => (
        <MoneyListItem key={item.id} item={item} onUpdate={onUpdate} onDelete={onDelete} />
      )) : <div className="empty"><XCircle size={15} /><span>还没有余额监控配置</span></div>}
    </div>
  );
}

function MoneyListItem({ item, onUpdate, onDelete }: {
  item: MoneyConfig;
  onUpdate: (item: MoneyConfig, body: MoneyThresholdPayload) => Promise<void>;
  onDelete: (item: MoneyConfig) => void;
}) {
  const [thresholdEnabled, setThresholdEnabled] = useState(item.threshold_enabled);
  const [thresholdAmount, setThresholdAmount] = useState(String(item.threshold_amount ?? 10));
  const thresholdText = item.threshold_enabled && item.threshold_amount !== null ? item.threshold_amount : "未启用";
  return (
    <div className="list-row">
      <div>
        <div className="row-name">{item.name}</div>
        <div className="row-detail">{item.sub2_name || item.base_url} · 提醒 {item.alert_user_id}</div>
        <div className="row-meta">余额 {item.last_balance ?? "未知"} · 阈值 {thresholdText} · {item.last_checked_at ? formatDate(item.last_checked_at) : "未检查"}</div>
        <form className="threshold-row" onSubmit={async (e) => {
          e.preventDefault();
          await onUpdate(item, { threshold_enabled: thresholdEnabled, threshold_amount: thresholdEnabled ? Number(thresholdAmount) : null });
        }}>
          <label className="inline-check">
            <input type="checkbox" checked={thresholdEnabled} onChange={(e) => setThresholdEnabled(e.target.checked)} />
            阈值提醒
          </label>
          <input aria-label={`${item.name} 余额阈值`} type="number" min="0" step="0.01"
            disabled={!thresholdEnabled} required={thresholdEnabled}
            value={thresholdAmount} onChange={(e) => setThresholdAmount(e.target.value)}
            style={{ minHeight: "34px", borderRadius: "7px", background: "var(--bg2)", border: "1px solid var(--border2)", color: "var(--text)", padding: "0 10px" }} />
          <button type="submit" className="btn-sm">保存阈值</button>
        </form>
        {item.last_error && <div className="row-error">{shortText(item.last_error, 160)}</div>}
      </div>
      <button type="button" className="btn-delete" title="删除" onClick={() => onDelete(item)}><Trash2 size={15} /></button>
    </div>
  );
}

/* ── CommandCatalog ────────────────────────────────────────── */
function CommandCatalog({ groups }: { groups: CommandGroup[] }) {
  return (
    <div className="command-grid">
      {groups.map((group) => (
        <div className="command-col" key={group.robot}>
          <h3>{group.robot}</h3>
          <p>{group.role}</p>
          <div className="command-list">
            {group.commands.length ? group.commands.map((cmd) => (
              <div className="command-row" key={cmd.name}>
                <code>{cmd.name}</code>
                <span style={{ fontSize: "12px", color: "var(--text2)" }}>{cmd.description}</span>
                <small>{cmd.scope}</small>
              </div>
            )) : <div className="empty"><XCircle size={14} /><span>没有业务命令</span></div>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Primitive UI components ───────────────────────────────── */
function MetricCard({ label, value, icon: Icon, tone }: {
  label: string; value: string;
  icon: React.ComponentType<{ size?: number }>;
  tone: string;
}) {
  return (
    <div className="metric-card">
      <div className={`metric-icon-wrap ${tone}`}><Icon size={20} /></div>
      <div className="metric-body">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function RobotCard({ title, status, detail, ok }: { title: string; status: string; detail: string; ok: boolean }) {
  return (
    <div className="robot-card">
      <div className="robot-card-icon">
        {ok
          ? <CheckCircle2 size={18} color="var(--green)" />
          : <AlertTriangle size={18} color="var(--amber)" />
        }
      </div>
      <div>
        <strong>{title}</strong>
        <p>{status}</p>
        <small>{detail}</small>
      </div>
    </div>
  );
}

function NoticeBar({ notice }: { notice: NonNullable<Notice> }) {
  return (
    <div className={`notice ${notice.tone}`} style={{ marginTop: 0, marginBottom: "16px" }}>
      {notice.tone === "ok" ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
      <span>{notice.text}</span>
    </div>
  );
}

function StatusPill({ tone, text }: { tone: "ok" | "warn" | "danger" | "neutral"; text: string }) {
  return <span className={`pill ${tone}`}>{text}</span>;
}

function StatusRail({ bar }: { bar: StatusBar }) {
  const win = bar.windows[0];
  if (!win) return null;
  return (
    <div className="status-rail" title={`${win.label} · ${bar.success_rate.toFixed(1)}%`}>
      {win.buckets.map((b, i) => (
        <span key={`${b.state}-${i}`} className={`bucket ${b.state}`} />
      ))}
    </div>
  );
}

function SelectField({ label, value, onChange, options, required }: {
  label: string; value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
}) {
  return (
    <div className="form-label">
      <span>{label}</span>
      <select required={required} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">请选择</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function CheckboxGrid({ title, emptyText, options, values, onChange }: {
  title: string; emptyText: string;
  options: { value: string; label: string }[];
  values: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (v: string) =>
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <div className="checkbox-block">
      <span>{title}</span>
      {options.length ? (
        <div className="checkbox-grid">
          {options.map((o) => (
            <label key={o.value} className="check-option">
              <input type="checkbox" checked={values.includes(o.value)} onChange={() => toggle(o.value)} />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      ) : <p>{emptyText}</p>}
    </div>
  );
}

/* ── Utility functions (unchanged) ────────────────────────── */
function optionFromSub2(item: Sub2Config) {
  return { value: String(item.id), label: item.name };
}

function errorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return "未知错误";
}

function formatDate(value: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).format(date);
}

function formatInterval(minutes: number) {
  if (minutes % 60 === 0) return `${minutes / 60} 小时`;
  return `${minutes} 分钟`;
}

function shortText(value: string, maxLength: number) {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1)}…`;
}

function statusTone(status: string): "ok" | "warn" | "danger" | "neutral" {
  if (status === "ok")      return "ok";
  if (status === "degraded") return "warn";
  if (status === "down")    return "danger";
  if (status === "unknown") return "warn";
  return "neutral";
}

function statusLabel(status: string) {
  if (status === "ok")       return "可用";
  if (status === "degraded") return "响应缓慢";
  if (status === "down")     return "中断";
  if (status === "unknown")  return "未知";
  return status;
}

function textValue(value: unknown) {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

function arrayText(value: unknown) {
  if (!Array.isArray(value) || !value.length) return "-";
  return value.map((v) => String(v)).join("、");
}

/* ── Mount ─────────────────────────────────────────────────── */
createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
